import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ApiService } from '../../services/api.service';
import { ConversationJson, ConversationMessage } from './chat-bot.models';

// â”€â”€â”€ PDF Layout Constants â”€â”€â”€â”€â”
const PDF_MARGIN      = 10;                           // mm â€“ all-side margin
const PDF_PAGE_W      = 210;                          // mm â€“ A4 width
const PDF_PAGE_H      = 297;                          // mm â€“ A4 height
const PDF_CONTENT_W   = PDF_PAGE_W - 2 * PDF_MARGIN; // 190 mm usable width
const BUBBLE_MAX_FRAC = 0.72;                         // bubble = 72 % of content width
const BUBBLE_PAD      = 3;                            // mm â€“ inner bubble padding
const MSG_FS          = 9;                            // pt â€“ message body font size
const SENDER_FS       = 7;                            // pt â€“ sender label font size
const TS_FS           = 7;                            // pt â€“ timestamp font size
const LINE_H          = 4.5;                          // mm â€“ line height for body text
const SENDER_H        = 5;                            // mm â€“ reserved for sender row
const TS_H            = 5;                            // mm â€“ reserved for timestamp row
const MSG_GAP         = 5;                            // mm â€“ gap between messages

@Injectable({ providedIn: 'root' })
export class ChatBotHelperService {

  constructor(private apiService: ApiService) {}

  // â”€â”€â”€ UNCHANGED: content formatters â”€â”€â”€â”€

  private formatMessageContent(content: string, inline = false): string {
    if (!content) return '';

    // Normalize line endings and collapse consecutive blank lines.
    content = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    content = content.replace(/\n\s*\n+/g, '\n');

    if (!inline) {
      return content
        .replace(/^# (.*)$/gm,     '<div class="pdf-h1">$1</div>')
        .replace(/^## (.*)$/gm,    '<div class="pdf-h2">$1</div>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g,     '<em>$1</em>')
        .replace(/`(.*?)`/g,       '<code>$1</code>')
        .replace(/\n/g,            '<br/>');
    }

    // Inline mode: no block-level wrappers (used inside table cells).
    return content
      .replace(/^# (.*)$/gm,     '<strong>$1</strong>')
      .replace(/^## (.*)$/gm,    '<strong>$1</strong>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g,     '<em>$1</em>')
      .replace(/`(.*?)`/g,       '<code>$1</code>')
      .replace(/\n/g,            '<br/>');
  }

  getMessageContent(msg: any): string {
    return this.formatMessageContent(msg.message || '');
  }

  // â”€â”€â”€ UNCHANGED: table HTML builder â”€â”€â”€â”€

  private buildTableHtml(msg: any): string {
    let html = `
      <div style=" width:100%; overflow:visible; page-break-inside:auto; break-inside:auto; ">
        <table style=" width:100%; max-width:100%; border-collapse:collapse; table-layout:fixed; margin-top:10px; font-size:10px; " >
          <thead style=" display:table-header-group; ">
            <tr>`;

    msg.ui.columns.forEach((col: any) => {
      html += `
        <th style=" border:1px solid #000; padding:6px; text-align:left; word-break:break-word; overflow-wrap:break-word; white-space:normal; background:#f5f5f5; ">
          ${col.header}
        </th>`;
    });

    html += `</tr> </thead> <tbody style="page-break-inside:auto;"> `;

    msg.ui.data.forEach((row: any) => {
      html += `<tr>`;
      msg.ui.columns.forEach((col: any) => {
        html += `
          <td style=" border:1px solid #000; padding:6px; word-break:break-word; overflow-wrap:break-word; white-space:normal; vertical-align:top; ">
            ${row[col.key] ? this.formatMessageContent(String(row[col.key]), true) : ''}
          </td>`;
      });
      html += `</tr>`;
    });

    html += ` </tbody> </table> </div>`;
    return html;
  }

  // â”€â”€â”€ UNCHANGED: JSON export â”€â”€â”€â”€â

  buildConversationJson(messages: any[]): ConversationJson {
    const exported: ConversationMessage[] = messages.map((msg) => ({
      sender:     msg.sender as 'user' | 'assistant',
      message:    this.getMessageContent(msg),
      uiType:     msg.uiType ?? null,
      timestamp:  msg.timestamp ? new Date(msg.timestamp) : null,
      formatTime: msg.formatTime ?? ''
    }));
    return { exportedAt: new Date().toISOString(), messages: exported };
  }

  // â”€â”€â”€ Plain-text extractor for jsPDF â”€â”€â”€â”€â”€â”€

  private getPlainText(msg: any): string {
    return (msg.message || '')
      .replace(/\r\n/g, '\n').replace(/\r/g, '\n')
      .replace(/<br\s*\/?>/gi, '\n')   // convert <br> / <br/> to newline
      .replace(/<[^>]+>/g, '')         // strip any remaining HTML tags
      .replace(/\n\s*\n+/g, '\n')
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/`(.*?)`/g, '$1')
      .trim();
  }

  // â”€â”€â”€ PDF: draw conversation header, returns y after header â”€

  private drawHeader(pdf: jsPDF, margin: number, contentW: number, exportedAt: string): number {
    let y = margin;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(25, 118, 210);
    pdf.text('Conversation', margin, y + 7);
    y += 10;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(102, 102, 102);
    pdf.text(exportedAt, margin, y + 4);
    y += 7;
    pdf.setDrawColor(25, 118, 210);
    pdf.setLineWidth(0.5);
    pdf.line(margin, y, margin + contentW, y);
    return y + 5;
  }

  // â”€â”€â”€ PDF: calculate message block height without drawing â”

  private calcBlockHeight(pdf: jsPDF, msg: any, contentW: number, includeTimestamp = true): number {
    const text    = this.getPlainText(msg);
    const bubbleW = contentW * BUBBLE_MAX_FRAC;
    const innerW  = bubbleW - 2 * BUBBLE_PAD;
    pdf.setFontSize(MSG_FS);
    const lines   = pdf.splitTextToSize(text || ' ', innerW);
    const bubbleH = lines.length * LINE_H + 2 * BUBBLE_PAD;
    return SENDER_H + bubbleH + (includeTimestamp ? TS_H : 0);
  }

  // â”€â”€â”€ PDF: draw one chat message block, returns new y â”€â

  private drawMessage(pdf: jsPDF, msg: any, y: number, margin: number, contentW: number, skipTimestamp = false): number {
    const isUser     = msg.sender === 'user';
    const label      = isUser ? 'You' : 'Iris';
    const text       = this.getPlainText(msg);
    const ts         = msg.formatTime ? (msg.formatTime.split(' - ')[1] || msg.formatTime) : '';
    const maxBubbleW = contentW * BUBBLE_MAX_FRAC;
    const innerW     = maxBubbleW - 2 * BUBBLE_PAD;
    const align      = isUser ? ('right' as const) : ('left' as const);
    const textX      = isUser ? margin + contentW : margin;

    // Sender label
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(SENDER_FS);
    pdf.setTextColor(100, 100, 100);
    pdf.text(label, textX, y + 3.5, { align });
    y += SENDER_H;

    // Word-wrap message text
    pdf.setFontSize(MSG_FS);
    const lines = pdf.splitTextToSize(text || ' ', innerW);

    // Auto-width: size bubble to content, capped at maxBubbleW
    const longestLineW = Math.max(...(lines as string[]).map((l: string) => pdf.getTextWidth(l)));
    const bubbleW  = Math.min(longestLineW + 2 * BUBBLE_PAD, maxBubbleW);
    const bubbleX  = isUser ? margin + contentW - bubbleW : margin;
    const bubbleH  = lines.length * LINE_H + 2 * BUBBLE_PAD;

    // Bubble background
    if (isUser) {
      pdf.setFillColor(25, 118, 210);
    } else {
      pdf.setFillColor(240, 240, 240);
    }
    pdf.roundedRect(bubbleX, y, bubbleW, bubbleH, 2, 2, 'F');

    // Bubble text
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(MSG_FS);
    if (isUser) {
      pdf.setTextColor(255, 255, 255);
    } else {
      pdf.setTextColor(51, 51, 51);
    }
    pdf.text(lines, bubbleX + BUBBLE_PAD, y + BUBBLE_PAD + LINE_H * 0.8);
    y += bubbleH + 1;

    // Timestamp — skipped for table messages (rendered after the table instead)
    if (!skipTimestamp) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(TS_FS);
      pdf.setTextColor(170, 170, 170);
      pdf.text(ts, textX, y + 3, { align });
      y += TS_H + MSG_GAP;
    }

    return y;
  }

  // ─── PDF: draw timestamp only (used after table rendering) ──────────────────

  private drawTimestamp(pdf: jsPDF, msg: any, y: number, margin: number, contentW: number): number {
    const isUser = msg.sender === 'user';
    const ts     = msg.formatTime ? (msg.formatTime.split(' - ')[1] || msg.formatTime) : '';
    const align  = isUser ? ('right' as const) : ('left' as const);
    const textX  = isUser ? margin + contentW : margin;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(TS_FS);
    pdf.setTextColor(170, 170, 170);
    pdf.text(ts, textX, y + 3, { align });
    return y + TS_H + MSG_GAP;
  }

  // â”€â”€â”€ PDF: render table via html2canvas, returns y after last slice â”

  private async renderTable(pdf: jsPDF, msg: any, currentY: number, margin: number, contentWmm: number): Promise<number> {
    const MIN_FIRST_SLICE = 20; // mm — minimum remaining height to start table on current page
    const contentWpx = Math.round(contentWmm * 96 / 25.4);

    const wrapper = document.createElement('div');
    wrapper.style.cssText = [
      `width:${contentWpx}px`,
      'position:absolute',
      'left:-9999px',
      'top:0',
      'font-family:Arial,sans-serif',
      'font-size:13px',
      'background:#fff'
    ].join(';');
    wrapper.innerHTML = this.buildTableHtml(msg);
    document.body.appendChild(wrapper);

    const canvas = await html2canvas(wrapper, { scale: 1.5, useCORS: true, logging: false });
    document.body.removeChild(wrapper);

    const fullPageAvailH = PDF_PAGE_H - 2 * margin;
    const pxPerMm        = canvas.width / contentWmm;
    const totalHmm       = canvas.height / pxPerMm;
    let   offsetMm       = 0;
    let   sliceY         = currentY;
    let   isFirst        = true;

    while (offsetMm < totalHmm) {
      let availH: number;

      if (isFirst) {
        availH = PDF_PAGE_H - currentY - margin;
        if (availH < MIN_FIRST_SLICE) {
          // Not enough room on current page — start on a fresh page
          pdf.addPage();
          sliceY = margin;
          availH = fullPageAvailH;
        }
        isFirst = false;
      } else {
        pdf.addPage();
        sliceY = margin;
        availH = fullPageAvailH;
      }

      const sliceHmm = Math.min(availH, totalHmm - offsetMm);
      const offsetPx = Math.round(offsetMm * pxPerMm);
      const sliceHpx = Math.round(sliceHmm * pxPerMm);

      const slice    = document.createElement('canvas');
      slice.width    = canvas.width;
      slice.height   = sliceHpx;
      slice.getContext('2d')!.drawImage(canvas, 0, -offsetPx);

      pdf.addImage(slice.toDataURL('image/jpeg', 0.85), 'JPEG', margin, sliceY, contentWmm, sliceHmm);
      offsetMm += sliceHmm;
      sliceY   += sliceHmm;
    }

    return sliceY;
  }

  // â”€â”€â”€ Public: generate PDF blob\

  async generateConversationPdf(messages: any[], exportedAt: string): Promise<Blob> {
    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    let y = this.drawHeader(pdf, PDF_MARGIN, PDF_CONTENT_W, exportedAt);

    for (const msg of messages) {
      if (msg.uiType === 'table') {
        // 1. Draw sender + bubble (no timestamp yet)
        if (msg.message) {
          const blockH = this.calcBlockHeight(pdf, msg, PDF_CONTENT_W, false);
          if (y + blockH > PDF_PAGE_H - PDF_MARGIN) {
            pdf.addPage();
            y = PDF_MARGIN;
          }
          y = this.drawMessage(pdf, msg, y, PDF_MARGIN, PDF_CONTENT_W, true);
        }
        // 2. Render table starting at current y — uses remaining space first
        y = await this.renderTable(pdf, msg, y, PDF_MARGIN, PDF_CONTENT_W);
        // 3. Draw timestamp after the table
        y = this.drawTimestamp(pdf, msg, y, PDF_MARGIN, PDF_CONTENT_W);
      } else {
        // Chat message: check if block fits, add page if needed
        const blockH = this.calcBlockHeight(pdf, msg, PDF_CONTENT_W);
        if (y + blockH > PDF_PAGE_H - PDF_MARGIN) {
          pdf.addPage();
          y = PDF_MARGIN;
        }
        y = this.drawMessage(pdf, msg, y, PDF_MARGIN, PDF_CONTENT_W);
      }
    }

    return pdf.output('blob') as Blob;
  }

  // â”€â”€â”€ Public: send to CSM (UNCHANGED) 

  async sendConversationToCsm(messages: any[], exportedAt: string): Promise<FormData> {
    const pdfBlob = await this.generateConversationPdf(messages, exportedAt);
    const formData = new FormData();
    formData.append('file', pdfBlob, 'chat-conversation.pdf');
    // const pdfUrl = URL.createObjectURL(pdfBlob);
    // window.open(pdfUrl, '_blank');
    return formData;
  }
}
